import {Input} from 'antd';
import {FormInstance} from 'antd/es/form';
import React from 'react';

import {InputWithPostButton} from 'components/@common/specific/input-with-post-button';
import {
    FILE_UPLOAD_EXTENSIONS,
    SIGN_UPLOAD_EXTENSIONS,
} from 'components/ais/monitor-services-card/monitor-service-dynamic-fields/model/constants';
import {
    FileUploader,
} from 'components/ais/monitor-services-card/monitor-service-dynamic-fields/ui/file-uploader/file-uploader';
import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';
import {InputNumber} from 'components/form/inputs/input-number';
import {DateCell} from 'components/table/columns/date-cell/date-cell';
import {IFieldMeta, EFieldType} from 'entities/metadata';
import {SignWithCryptoProInput} from 'features/sign-with-crypto-pro';
import {IDynamicInputField} from 'shared/types';
import {EDocumentsSigningMethod} from 'store/slices/documents-uploading-slice';

interface IDynamicFieldResolverArgs extends Pick<IFieldMeta, 'requestDateFormat' | 'dateFormat'> {
    form: FormInstance;
    fieldKey: string | number;
    documentsSigningMethod?: EDocumentsSigningMethod;
    entityName: string;
    type?: EFieldType;
    isDisabled?: boolean;
    defaultValue?: string;
    referenceUrl?: string;
    placeholder?: string;
    useFirstOptionAsDefaultValue?: boolean;
    buttonTitle?: string;
}

interface IGetFileFieldPropsArgs {
    accept: string;
    disabled: boolean;
    multiple?: boolean;
}

const getFileFieldProps = (
    type: EFieldType,
    isDisabled?: boolean,
): IGetFileFieldPropsArgs => {
    const uploaderProps: IGetFileFieldPropsArgs = {
        disabled: isDisabled || false,
        accept: FILE_UPLOAD_EXTENSIONS,
    };
    if (type === EFieldType.FILE_LIST_WITH_SIGNS) {
        uploaderProps.accept = SIGN_UPLOAD_EXTENSIONS;
    }
    uploaderProps.multiple = type === EFieldType.FILE_LIST || type === EFieldType.FILE_LIST_WITH_SIGNS;
    return uploaderProps;
};

export const dynamicFieldResolver = ({
    documentsSigningMethod,
    entityName,
    form,
    type,
    isDisabled,
    defaultValue,
    placeholder,
    referenceUrl,
    dateFormat,
    requestDateFormat,
    fieldKey,
    useFirstOptionAsDefaultValue,
    buttonTitle,
}: IDynamicFieldResolverArgs) => {
    switch (type) {
    case EFieldType.TEXT:
    case EFieldType.STRING: {
        return (
            <Input
                defaultValue={defaultValue}
                disabled={isDisabled}
                placeholder={placeholder}
            />
        );
    }
    case EFieldType.NUMBER: {
        return (
            <InputNumber
                disabled={isDisabled}
                placeholder={placeholder}
            />
        );
    }
    case EFieldType.VALUESET:
    case EFieldType.MULTIVALUESET:
    case EFieldType.REFERENCE: {
        return (
            <CustomSelect
                settings={{
                    mode: type === EFieldType.MULTIVALUESET ? ECustomSelectMode.multiple : undefined,
                    url: referenceUrl,
                    labelPath: 'meaning',
                    valuePath: entityName === 'administration.monitor.programs' ? 'id' : 'lookupCode',
                    isClearable: true,
                    showSearch: true,
                    placeholder,
                    isDisabled,
                    useFirstOptionAfterReceivedFromFetcher: useFirstOptionAsDefaultValue,
                }}
            />
        );
    }
    case EFieldType.FILE:
    case EFieldType.FILE_LIST:
    case EFieldType.MULTIPART_FILE:
    case EFieldType.LIST_MULTIPART_FILE:
    case EFieldType.FILE_LIST_WITH_SIGNS: {
        const fileFieldProps = getFileFieldProps(
            type,
            isDisabled,
        );
        if (documentsSigningMethod === EDocumentsSigningMethod.CRYPTO_PRO_PLUGIN && fieldKey === 'signs') {
            return (
                <SignWithCryptoProInput
                    form={form}
                    fileFieldNames={['files', 'powerOfAttorney', 'mainDocument']}
                    entityName={entityName}
                />
            );
        }
        return <FileUploader {...fileFieldProps} />;
    }
    case EFieldType.DATE: {
        return (
            <DateCell
                requestFormat={requestDateFormat ?? 'DD.MM.YYYY'}
                format={dateFormat ?? 'DD.MM.YYYY'}
            />
        );
    }

    case EFieldType.INPUT_WITH_POST_BUTTON: {
        return referenceUrl ? (
          <InputWithPostButton
            formInstance={form}
            defaultValue={defaultValue}
            disabled={isDisabled}
            placeholder={placeholder}
            url={referenceUrl}
            buttonTitle={buttonTitle}
            fieldKey={fieldKey}
          />
        ) : null;
    }

    default: {
        return null;
    }
    }
};

export const dynamicFieldsSorter = (a?: IDynamicInputField, b?: IDynamicInputField) => (
    a?.displayOrder && b?.displayOrder ? a.displayOrder - b.displayOrder : 0
);
